<template>
  <article class="container-summary">
    <div class="paper-size">
      <header v-if="selectedAgent">
        <img
          src="../assets/logo.png"
          class="summary-logo">
        <div class="headline">
          <h1 class="is-size-3 has-text-weight-semibold">
            {{ selectedAgent.trivial_name | capitalizeFirstLetter }}
          </h1>
        </div>
        <div class="warning-labels">
          <div class="summary-firediamond">
            <fire-diamond
              v-if="agentFireDiamond"
              class="has-text-centered-mobile"
              :input="agentFireDiamond"
              :size="125"
            />
          </div>
          <div class="orange-shield">
            <div>
              {{ selectedAgent.hazard_id }}
            </div>
            <div>
              <span
                v-for="(un,idx) in selectedAgent.un_numbers"
                :key="idx"
              >
                {{ un.un_number }}
              </span>
            </div>
          </div>
          <div class="pictograms">
            <div
              class="pictogram"
              v-for="pictogram in agentPictograms"
              :key="pictogram.code"
            >
              <b-columns
                class="is-vcentered"
                is-multiline
                is-mobile
              >
                <b-column class="is-narrow">
                  <figure class="image is-48x48">
                    <img
                      :src="pictogram.pictogram"
                      :alt="pictogram.code"
                    >
                  </figure>
                </b-column>
                <b-column>
                  <p>{{ `${pictogram.code} ${pictogram.description}` }}</p>
                </b-column>
              </b-columns>
            </div>
          </div>
        </div>
        <div>
          <table class="table-id-numbers">
            <tr>
              <th>CAS Number</th>
              <th>{{ selectedAgent.cas_number }}</th>
            </tr>
            <tr>
              <th>Index Number</th>
              <th/>
            </tr>
            <tr>
              <th>EINECS Number</th>
              <th>
                <span
                  v-for="(ec,idx) in selectedAgent.ec_numbers"
                  :key="idx"
                >
                  {{ ec.ec_number }}
                </span>
              </th>
            </tr>
            <tr>
              <th>UN Number</th>
              <th>
                <span
                  v-for="(un,idx) in selectedAgent.un_numbers"
                  :key="idx"
                >
                  {{ un.un_number }}
                </span>
              </th>
            </tr>
            <tr>
              <th>Molecular formula</th>
              <th>{{ selectedAgent.molecular_formula }}</th>
            </tr>
          </table>
        </div>
        <div class="properties">
          <h2 class="is-size-6 has-text-weight-semibold">Physical - Chemical properties</h2>
          <table class="table-properties">
            <tr>
              <th>Molecular weight</th>
              <th>{{ selectedAgent.molecular_weight }}</th>
            </tr>
            <tr>
              <th>Density (liquefied)</th>
              <th>{{ selectedAgent.density }}</th>
            </tr>
            <tr>
              <th>Melting point</th>
              <th>{{ selectedAgent.melting_point }}</th>
            </tr>
            <tr>
              <th>Boiling point</th>
              <th>{{ selectedAgent.boiling_point }}</th>
            </tr>
            <tr>
              <th>Vapour pressure</th>
              <th>{{ selectedAgent.vapour_pressure }}</th>
            </tr>
            <tr>
              <th>Density (gas)</th>
              <th>{{ selectedAgent.relative_vapour_density }}</th>
            </tr>
            <tr>
              <th>Solubility</th>
              <th>{{ selectedAgent.solubility }}</th>
            </tr>
            <tr>
              <th>Conversion factors (fume) @ 20 °C</th>
              <th>{{ selectedAgent.conversion_rate }}</th>
            </tr>
            <tr>
              <th>Odor threshold</th>
              <th>{{ selectedAgent.odor_threshold }}</th>
            </tr>
          </table>
        </div>
        <p>
          <pdf-link
            v-if="selectedAgent.info_pdf"
            :url="selectedAgent.info_pdf"
          />
        </p>
        <div class="border-box">
          <h3>Protection</h3>
          <agent-protection :is-dense="true" />
        </div>
        <div>
          <agent-rescue
            :omit-fire-diamond="true"
            :is-dense="true"
            class="border-box" />
        </div>
      </header>
    </div>
  </article>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { rolesEnum, agentTypeEnum } from '@/store'
import CollapsibleCard from '@/components/structures/CollapsibleCard'
import AgentProtection from '@/components/agent/AgentProtection'
import AgentRescue from '@/components/agent/rescue/AgentRescue'
import FireDiamond from '@/components/agent/rescue/FireDiamond'
import AgentDecontamination from '@/components/agent/decontamination/AgentDecontamination'
import AgentEvacuation from '@/components/agent/AgentEvacuation'
import AgentTreatment from '@/components/agent/treatment/AgentTreatment'
import AgentDetectors from '@/components/agent/detectors/AgentDetectors'
import AgentSampling from '@/components/agent/sampling/AgentSampling'
import AgentFeatures from '@/components/agent/features/AgentFeatures'
import AgentGhsPictograms from '@/components/agent/features/attributes/chemical/AgentGhsPictograms'
import AgentLinks from '@/components/agent/links/AgentLinks'
import { colorByAgentCategory } from '@/mixins'

export default {
  components: {
    CollapsibleCard,
    AgentEvacuation,
    AgentProtection,
    AgentRescue,
    AgentDecontamination,
    AgentTreatment,
    AgentDetectors,
    AgentSampling,
    AgentFeatures,
    AgentLinks,
    FireDiamond,
    AgentGhsPictograms
  },
  props: {
    slug: {
      type: String,
      required: true,
    }
  },
  data () {
    return {
      roles: rolesEnum
    }
  },
  mounted () {
    this.fetchAgent(this.slug)
  },
  computed: {
    ...mapGetters([
      'selectedRole',
      'loadingAgents',
      'selectedAgent',
      'agentCategoryLabel',
      'agentFireDiamond',
      'agentPictograms'
    ]),
    isRNAgent () {
      return this.selectedAgent ? this.selectedAgent.agent_type === agentTypeEnum.RNAgent : false
    },
    isBAgent () {
      return this.selectedAgent ? this.selectedAgent.agent_type === agentTypeEnum.BAgent : false
    },
    isCAgent () {
      return this.selectedAgent ? this.selectedAgent.agent_type === agentTypeEnum.CAgent : false
    },
    isBioMarker () {
      return this.selectedAgent ? this.selectedAgent.agent_type === agentTypeEnum.BioMarker : false
    },
    getLayout () {
      const open = true
      const closed = false
      switch (this.selectedRole) {
        case rolesEnum.FIRST_RESPONDER:
          return [open, open, open, open, closed, closed, closed]
        case rolesEnum.DECON_OR_SAMPLER:
          return [open, closed, closed, closed, open, open, open]
      }
    }
  },
  mixins: [colorByAgentCategory],
  methods: {
    ...mapActions(['fetchAgent']),
  }
}
</script>

<style lang="scss">
  .tag {
    vertical-align: middle;
  }

  .container-summary {
    display: flex;
    justify-content: center;
    background-color: white;
    margin-top: -20px;
  }

  .paper-size {
    width: 877px;
    background-color: white;
    @media print {
      font-size: 14px;
    }
  }

  .summary-logo {
    width: 200px;
    position: absolute;
  }

  .headline {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .table-id-numbers {
    th {
      border: 1px solid black;
      width: 200px;
    }
  }

  .warning-labels {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    padding: 20px 0;
  }

  .summary-firediamond {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .orange-shield {
    display: flex;
    flex-direction: column;
    flex: 1;
    font-size: 24px;
    font-weight: bold;
    div {
      display: flex;
      justify-content: center;
      background-color: orange;
      border: 2px solid black;
      color: black;
      padding: 10px 0;
      width: 150px;
    }
  }

  .pictograms {
    display: flex;
    flex: 2;
    flex-direction: column;
    flex-wrap: wrap;
    max-height: 150px;
  }

  .pictogram {
    display: flex;
    font-size: 14px;
    max-width: 200px;
    @media print {
      font-size: 14px;
    }
  }

  .properties {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .table-properties {
    th {
      border: 1px solid black;
      width: 300px;
    }
  }

  .border-box {
    border: 1px solid black;
    padding: 10px 10px;
    margin-top: 10px;
    h3 {
      font-size: 20px;
    }
  }

</style>
